import { useEffect, useState } from 'react'
import { type_GM_STATE } from '../../helpers'
import { emitterProps, globalEmitter } from '../helpers/emitter'
import { refreshWishlistStore, useWishlistVariantMembership } from '../helpers/list-store'
import { openModal } from '../helpers/useModal'
import { wishlistProviderService } from '../helpers/provider.service'
import { HeartIcon } from '../icons/HeartIcon'
import { type_WISHLIST_DASHBOARD_STATE, typeProduct } from '../types'
import { LoaderSpin } from './LoaderSpin'

type props = {
  product: typeProduct
  productBiLine: string
  variantSelected: any
  variantSelectedId: number
  templateSuffix: string
  className?: string
  disableWishlist: string
  errorMessage: string
  propertiesLocal?: any[]
  isShowOnlyIcon: boolean
  productPersonalisedPrice?: string
  onRequestAdd?: () => void
  useProductSlideout?: boolean
  cartVariant?: 'icon' | 'text' | null
  addLabel?: string | null
}

declare var GM_STATE: type_GM_STATE
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

export function WishlistButton({
  product,
  productBiLine,
  variantSelected,
  variantSelectedId,
  templateSuffix,
  className = '',
  disableWishlist,
  errorMessage,
  propertiesLocal,
  isShowOnlyIcon,
  productPersonalisedPrice = '0',
  onRequestAdd,
  useProductSlideout = false,
  cartVariant = null,
  addLabel = null
}: props) {
  const resolvedVariantSelected = variantSelected || product?.selected_or_first_available_variant || product?.variants?.[0] || null
  const resolvedVariantSelectedId = variantSelectedId || resolvedVariantSelected?.id || 0
  const canOpenWithoutVariantSelection = useProductSlideout
  const hasPendingVariantSelection = canOpenWithoutVariantSelection && disableWishlist === 'true'
  const effectiveDisableWishlist = canOpenWithoutVariantSelection ? 'false' : disableWishlist

  if (!product || (!canOpenWithoutVariantSelection && (!resolvedVariantSelectedId || !resolvedVariantSelected))) {
    return
  }
  const [isLoading, setIsLoading] = useState(false)
  const [properties, setProperties] = useState<any>([])
  const isWishlistDisabled = disableWishlist === 'true'
  const membership = useWishlistVariantMembership(resolvedVariantSelectedId, properties, useProductSlideout)
  const currentItem = membership?.item || null
  const currentList = membership?.list || null
  const isCurrentItemVisible = Boolean(currentItem) && !hasPendingVariantSelection
  const showDisabledHoverState = isWishlistDisabled && !isCurrentItemVisible && !canOpenWithoutVariantSelection
  const showDisabledErrorMessage = effectiveDisableWishlist === 'true' && Boolean(errorMessage) && !isCurrentItemVisible && !isShowOnlyIcon
  const wishlistSlideoutEl = document.querySelector('.js-wishlist-slideout')
  const wishlistProductSlideoutEl = document.querySelector('.js-wishlist-product-slideout')

  const openSlideout = () => {
    if (effectiveDisableWishlist === 'true') {
      document.dispatchEvent(new CustomEvent('add-wishlist-error'))
      return
    }

    const canOpenWishlist = wishlistProviderService.isCustomerLoggedIn()

    if (canOpenWishlist) {
      if (useProductSlideout && wishlistProductSlideoutEl) {
        wishlistProductSlideoutEl?.setAttribute('product-id', product.id.toString())
        wishlistProductSlideoutEl?.setAttribute('product-handle', product.handle)
        wishlistProductSlideoutEl?.setAttribute('variant-selected-id', resolvedVariantSelectedId ? resolvedVariantSelectedId.toString() : '')
        wishlistProductSlideoutEl?.setAttribute('template-suffix', templateSuffix)
        wishlistProductSlideoutEl?.setAttribute('product-personalised-price', productPersonalisedPrice)
        wishlistProductSlideoutEl?.setAttribute('open-source', 'pdp')
        openModal('wishlist-product-slideout')
        document.body.classList.remove('no-scroll')
        return
      }

      wishlistSlideoutEl?.setAttribute('product', JSON.stringify(product))
      wishlistSlideoutEl?.setAttribute('variant-selected', JSON.stringify(resolvedVariantSelected))
      wishlistSlideoutEl?.setAttribute('product-bi-line', productBiLine)
      wishlistSlideoutEl?.setAttribute('variant-selected-id', resolvedVariantSelectedId.toString())
      wishlistSlideoutEl?.setAttribute('template-suffix', templateSuffix)
      wishlistSlideoutEl?.setAttribute('product-personalised-price', productPersonalisedPrice)
      if (propertiesLocal) {
        wishlistSlideoutEl?.setAttribute('properties-local', JSON.stringify(propertiesLocal))
      }
      openModal('wishlist-slideout')
      document.body.classList.remove('no-scroll')
    } else {
      let valueReturnTo = `/products/${product.handle}`

      if (resolvedVariantSelectedId) {
        valueReturnTo = valueReturnTo + `?variant=${resolvedVariantSelectedId.toString()}`
      }

      if (properties.length > 0) {
        const isPairKey = properties.some((property: any) => property.hasOwnProperty('pair'))

        if (isPairKey) {
          const isPair = properties.find((property: any) => property.hasOwnProperty('pair'))?.pair
          valueReturnTo = valueReturnTo + `&pair=${isPair.toString()}`
        }
      }

      if (cartVariant) {
        valueReturnTo = '/account?view=wishlist#view=lists'
      }

      sessionStorage.setItem('returnTo', valueReturnTo)
      openModal('account-slideout')
    }
  }

  const removeWishlistItem = () => {
    if (!currentList || !currentItem) {
      return
    }
    setIsLoading(true)
    wishlistProviderService.removeItemFromList(currentList, currentItem).then((result) => {
      if (!result) {
        setIsLoading(false)
        globalEmitter.emit('triggerNotification', { message: 'Item could not be removed to wishlist', type: 'error' })
        return
      }

      setIsLoading(false)
      refreshWishlistStore().catch((error) => {
        console.warn('Could not refresh wishlist store after item removal', error)
      })
      globalEmitter.emit('triggerNotification', { message: 'Item removed successfully', type: 'success' })
      globalEmitter.emit(emitterProps.wishlist.remove, { listHandle: currentList.handle, itemId: currentItem.id })
    })
  }

  const handleButtonClick = () => {
    if (hasPendingVariantSelection) {
      if (onRequestAdd) {
        onRequestAdd()
        return
      }

      openSlideout()
      return
    }

    if (currentItem) {
      removeWishlistItem()
    } else {
      if (onRequestAdd) {
        onRequestAdd()
        return
      }

      openSlideout()
    }
  }

  useEffect(() => {
    const handleAddToWishlist = (values: { addQueue: any[] }) => {
      setProperties(values.addQueue)
    }

    const detachAddToWishlist = globalEmitter.on('product:addToWishlist', handleAddToWishlist)

    return () => {
      detachAddToWishlist()
    }
  }, [])

  useEffect(() => {
    if (propertiesLocal) {
      setProperties(propertiesLocal)
    }
  }, [propertiesLocal])

  if (cartVariant) {
    return (
      <button
        className={`js-button-wishlist-product CartItem__WishlistButton CartItem__WishlistButton--${cartVariant} ${
          cartVariant === 'text' ? 'Button Button--quaternary-dark' : ''
        }`}
        type="button"
        onClick={handleButtonClick}
        disabled={isLoading}
        aria-busy={isLoading}
        aria-label={isCurrentItemVisible ? wishlistDashboardState.removeFromWishlist : wishlistDashboardState.addToWishlist}
      >
        {cartVariant === 'icon' ? (
          <HeartIcon className={`js-icon-heart-product ${isCurrentItemVisible ? 'fill-ink' : 'text-subdued'}`} />
        ) : isCurrentItemVisible ? (
          wishlistDashboardState.removeFromWishlist
        ) : (
          addLabel || wishlistDashboardState.addToWishlist
        )}
      </button>
    )
  }

  return (
    <button
      className={`relative js-button-wishlist-product ${className} ${
        !isShowOnlyIcon ? 'Button Button--tertiary flex justify-center items-center w-full overflow-hidden group' : 'mt-0'
      }`}
      type="button"
      onClick={handleButtonClick}
    >
      {!isLoading ? (
        !isShowOnlyIcon ? (
          <span
            className={`relative flex justify-center items-center gap-3 w-full h-full ${
              showDisabledHoverState
                ? 'opacity-100 transition-all duration-200 ease-in-out group-hover:opacity-0 group-hover:-translate-y-[30px]'
                : ''
            }`}
          >
            {isCurrentItemVisible ? wishlistDashboardState.removeFromWishlist : wishlistDashboardState.addToWishlist}
          </span>
        ) : (
          <HeartIcon className={`js-icon-heart-product ${isCurrentItemVisible ? 'fill-ink' : 'text-subdued'}`} />
        )
      ) : (
        <LoaderSpin />
      )}
      {showDisabledErrorMessage && (
        <span className="absolute bottom-[-15px] left-1/2 -translate-x-1/2 -translate-y-1/2 h-fit opacity-0 transition-all duration-200 ease-in-out group-hover:bottom-[5px] group-hover:opacity-100">
          {errorMessage}
        </span>
      )}
    </button>
  )
}

